import {
  subscribe,
  getState,
  addSessionLocal,
  updateSessionLocal,
  removeSessionLocal,
  addNeverForgetEventLocal,
  updateNeverForgetEventLocal,
  updateCharacterLocal
} from '../state/store.js';
import {
  createSession,
  updateSession,
  deleteSession,
  createNeverForgetEvent,
  updateNeverForgetEvent,
  updateCharacter
} from '../services/supabase.js';
import { parseDateOnly, formatDateForDisplayValue, formatDateForInputValue } from '../utils/dates.js';
import { initFormPillGroups, renderSingleSelectPillGroup, updateFormPillSummary } from '../utils/pill-select.js';
import { getLocationOptions } from '../utils/locations.js';
import { CAKE_SVG, SKULL_SVG, CANDLE_SVG, QUILL_SVG, HOURGLASS_SVG, MOON_NEW_SVG, MOON_FULL_SVG } from '../utils/event-icons.js';
import { getMoonMarker } from '../utils/moon.js';

const calendarGrid = document.getElementById('calendarGrid');
const calendarMonthLabel = document.getElementById('calendarMonthLabel');
const calendarPrevBtn = document.getElementById('calendarPrevBtn');
const calendarNextBtn = document.getElementById('calendarNextBtn');
const calendarTodayBtn = document.getElementById('calendarTodayBtn');
const calendarDetailRail = document.getElementById('calendarDetailRail');

const logSessionBtn = document.getElementById('logSessionBtn');
const logSessionModal = document.getElementById('logSessionModal');
const logSessionModalTitle = document.getElementById('logSessionModalTitle');
const logSessionSubmitBtn = document.getElementById('logSessionSubmitBtn');
const logSessionForm = document.getElementById('logSessionForm');
const logSessionCancel = document.getElementById('logSessionCancel');
const charactersPresentGroup = logSessionModal
  ? logSessionModal.querySelector('[data-form-characters-select]')
  : null;
const sessionLocationGroup = logSessionModal
  ? logSessionModal.querySelector('[data-form-location-select]')
  : null;

let editingSessionId = null;

const neverForgetBtn = document.getElementById('neverForgetBtn');
const neverForgetModal = document.getElementById('neverForgetModal');
const neverForgetForm = document.getElementById('neverForgetForm');
const neverForgetCancel = document.getElementById('neverForgetCancel');
const neverForgetModalTitle = document.getElementById('neverForgetModalTitle');
const neverForgetSubmitBtn = document.getElementById('neverForgetSubmitBtn');

let editingNeverForgetId = null;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

let viewYear;
let viewMonth;
let selectedDate = null;

const getToday = () => new Date();

const ordinal = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const isDeadStatus = (status) => String(status || '').trim().toLowerCase() === 'dead';
const isAliveStatus = (status) => String(status || '').trim().toLowerCase() === 'alive';

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const EVENT_ICONS = {
  anniversary: `<span class="evt-icon">${CAKE_SVG}</span>`,
  deathday: `<span class="evt-icon">${SKULL_SVG}</span>`,
  timejail: `<span class="evt-icon">${HOURGLASS_SVG}</span>`,
  session: `<span class="evt-icon">${QUILL_SVG}</span>`,
  neverforget: `<span class="evt-icon">${CANDLE_SVG}</span>`
};

const MOON_LABELS = { new: 'New Moon', full: 'Full Moon' };
const MOON_ICONS = { new: MOON_NEW_SVG, full: MOON_FULL_SVG };

const formatSessionLabel = (session) =>
  session?.session_number ? `Session ${session.session_number} Played` : 'Session Played';

const eventLabel = (evt) => {
  if (evt.type === 'session') return formatSessionLabel(evt.session);
  if (evt.type === 'neverforget') return evt.title;
  return evt.name;
};

const buildMonthEvents = (characters, sessions, neverForgetEvents, year, month) => {
  const map = new Map();
  const add = (day, event) => {
    const list = map.get(day) || [];
    list.push(event);
    map.set(day, list);
  };

  characters.forEach((c) => {
    // The initial Birthday and First Adventure always appear, on the exact
    // day they happened. Once that day rolls into a later year, it becomes
    // a recurring anniversary — shown only while the character is Alive.
    const bd = parseDateOnly(c.birthday);
    if (bd && bd.month - 1 === month) {
      const years = year - bd.year;
      if (years === 0) {
        add(bd.day, { type: 'anniversary', title: 'Birthday', name: c.name });
      } else if (years > 0 && isAliveStatus(c.status)) {
        add(bd.day, { type: 'anniversary', title: `${ordinal(years)} Birthday`, name: c.name });
      }
    }

    const fa = parseDateOnly(c.first_adventure);
    if (fa && fa.month - 1 === month) {
      const years = year - fa.year;
      if (years === 0) {
        add(fa.day, { type: 'anniversary', title: 'First Adventure', name: c.name });
      } else if (years > 0 && isAliveStatus(c.status)) {
        add(fa.day, {
          type: 'anniversary',
          title: `${ordinal(years)} Year Adventuring Anniversary`,
          name: c.name
        });
      }
    }

    const dd = parseDateOnly(c.deathday);
    if (dd && dd.month - 1 === month && year >= dd.year && isDeadStatus(c.status)) {
      const years = year - dd.year;
      const title = years > 0 ? `Deathday Anniversary — ${ordinal(years)}` : 'Deathday';
      add(dd.day, {
        type: 'deathday',
        title,
        name: c.name,
        causeOfDeath: c.cause_of_death || ''
      });
    }

    const tj = parseDateOnly(c.time_jail);
    if (tj && tj.year === year && tj.month - 1 === month) {
      add(tj.day, { type: 'timejail', title: 'Time Jail Exit', name: c.name });
    }
  });

  sessions.forEach((s) => {
    const sd = parseDateOnly(s.session_date_played);
    if (sd && sd.year === year && sd.month - 1 === month) {
      add(sd.day, { type: 'session', title: formatSessionLabel(s), session: s });
    }
  });

  neverForgetEvents.forEach((evt) => {
    const ed = parseDateOnly(evt.event_date);
    if (!ed || ed.month - 1 !== month || year < ed.year) return;
    const years = year - ed.year;
    const title = years > 0 ? `${evt.event_name} — ${ordinal(years)} Anniversary` : evt.event_name;
    add(ed.day, { type: 'neverforget', title, description: evt.description || '', id: evt.id });
  });

  return map;
};

const renderDetailRail = (eventsByDay) => {
  if (!calendarDetailRail) return;
  const today = getToday();

  let day = null;
  if (selectedDate) {
    const parsed = parseDateOnly(selectedDate);
    if (parsed && parsed.year === viewYear && parsed.month - 1 === viewMonth) day = parsed.day;
  }
  if (day == null && viewYear === today.getFullYear() && viewMonth === today.getMonth()) {
    day = today.getDate();
  }

  if (day == null) {
    calendarDetailRail.innerHTML = '<p class="muted">Select a day to see its details.</p>';
    return;
  }

  const events = eventsByDay.get(day) || [];
  const dateLabel = new Date(viewYear, viewMonth, day).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (!events.length) {
    calendarDetailRail.innerHTML = `
      <div class="detail-rail-eyebrow">Selected Day</div>
      <div class="detail-rail-date">${escapeHTML(dateLabel)}</div>
      <p class="muted">No events recorded for this day.</p>
    `;
    return;
  }

  const sections = events.map((evt) => {
    if (evt.type === 'session') {
      const s = evt.session;
      const chars = String(s.characters_present || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      const pillsHtml = chars.length
        ? chars.map((name) => `<span class="pill-static">${escapeHTML(name)}</span>`).join('')
        : '<span class="muted">None recorded</span>';
      const inGameDate = s.in_game_session_ended
        ? escapeHTML(formatDateForDisplayValue(s.in_game_session_ended))
        : '—';
      const notes = s.notes ? escapeHTML(s.notes) : 'No notes.';
      return `
        <div class="detail-session-header">
          <span class="detail-session-header-label">${EVENT_ICONS.session} ${escapeHTML(formatSessionLabel(s))}</span>
          <span class="detail-session-actions" data-dm-only>
            <button type="button" class="btn small ghost detail-edit-btn" data-edit-session-id="${s.id}">Edit</button>
            <button type="button" class="btn small ghost detail-edit-btn" data-delete-session-id="${s.id}">Delete</button>
          </span>
        </div>
        <div class="detail-field">
          <span class="detail-field-label">In-Game — Session Ended</span>
          <span class="detail-field-value">${inGameDate}</span>
        </div>
        <div class="detail-field">
          <span class="detail-field-label">Characters Present</span>
          <div class="detail-pills">${pillsHtml}</div>
        </div>
        <div class="detail-field">
          <span class="detail-field-label">Notes</span>
          <p class="detail-notes">${notes}</p>
        </div>
      `;
    }
    if (evt.type === 'neverforget') {
      return `
        <div class="detail-session-header">
          <span class="detail-session-header-label">${EVENT_ICONS.neverforget} ${escapeHTML(evt.title)}</span>
          <button type="button" class="btn small ghost detail-edit-btn" data-edit-neverforget-id="${evt.id}">Edit</button>
        </div>
        <div class="detail-field">
          <p class="detail-notes">${evt.description ? escapeHTML(evt.description) : 'No description provided.'}</p>
        </div>
      `;
    }
    const causeOfDeathHtml =
      evt.type === 'deathday' && evt.causeOfDeath
        ? `<div class="detail-field">
            <span class="detail-field-label">Cause of Death</span>
            <p class="detail-notes">${escapeHTML(evt.causeOfDeath)}</p>
          </div>`
        : '';
    return `
      <div class="detail-field">
        <span class="detail-field-label">${escapeHTML(evt.title)}</span>
        <span class="detail-field-value">${escapeHTML(evt.name)}</span>
      </div>
      ${causeOfDeathHtml}
    `;
  });

  calendarDetailRail.innerHTML = `
    <div class="detail-rail-eyebrow">Selected Day</div>
    <div class="detail-rail-date">${escapeHTML(dateLabel)}</div>
    ${sections.join('<hr class="detail-divider">')}
  `;
};

const renderCalendar = () => {
  if (!calendarGrid) return;
  const { characters, sessions, neverForgetEvents } = getState();
  const eventsByDay = buildMonthEvents(characters, sessions || [], neverForgetEvents || [], viewYear, viewMonth);
  const today = getToday();

  if (calendarMonthLabel) calendarMonthLabel.textContent = `${MONTH_NAMES[viewMonth]} ${viewYear}`;

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  let html = WEEKDAY_NAMES.map((d) => `<div class="cal-weekday">${d}</div>`).join('');

  for (let i = firstWeekday - 1; i >= 0; i--) {
    html += `<div class="cal-day outside"><div class="cal-day-num">${daysInPrevMonth - i}</div></div>`;
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayEvents = eventsByDay.get(day) || [];
    const classes = ['cal-day'];
    if (viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate()) {
      classes.push('today');
    }
    if (selectedDate === dateStr) classes.push('selected');

    const moonPhase = getMoonMarker(viewYear, viewMonth, day);
    const moonHtml = moonPhase
      ? `<span class="cal-day-moon cal-day-moon-${moonPhase}" title="${MOON_LABELS[moonPhase]}">${MOON_ICONS[moonPhase]}</span>`
      : '';

    const eventsHtml = dayEvents
      .map(
        (evt) =>
          `<span class="evt evt-${evt.type}" title="${escapeHTML(evt.title)} — ${escapeHTML(eventLabel(evt))}">${EVENT_ICONS[evt.type]}<span class="evt-label">${escapeHTML(eventLabel(evt))}</span></span>`
      )
      .join('');
    const tagsHtml = dayEvents
      .map(
        (evt) =>
          `<span class="cal-tag-dot evt-${evt.type}" title="${escapeHTML(evt.title)} — ${escapeHTML(eventLabel(evt))}"></span>`
      )
      .join('');

    html += `<div class="${classes.join(' ')}" data-date="${dateStr}">
      <div class="cal-day-num">${day}</div>
      ${moonHtml}
      ${dayEvents.length ? `<div class="cal-day-events">${eventsHtml}</div>` : ''}
      ${dayEvents.length ? `<div class="cal-day-tags">${tagsHtml}</div>` : ''}
    </div>`;
  }

  const totalCells = firstWeekday + daysInMonth;
  const remaining = (7 - (totalCells % 7)) % 7;
  for (let day = 1; day <= remaining; day++) {
    html += `<div class="cal-day outside"><div class="cal-day-num">${day}</div></div>`;
  }

  calendarGrid.innerHTML = html;
  renderDetailRail(eventsByDay);
};

const goToToday = () => {
  const today = getToday();
  viewYear = today.getFullYear();
  viewMonth = today.getMonth();
  selectedDate = null;
  renderCalendar();
};

let calendarInitialized = false;
export const renderCalendarTab = () => {
  if (!calendarInitialized) {
    goToToday();
    calendarInitialized = true;
  } else {
    renderCalendar();
  }
};

const renderCharactersPresentPills = () => {
  if (!charactersPresentGroup) return;
  const { characters } = getState();
  const list = charactersPresentGroup.querySelector('.pill-list');
  if (!list) return;
  list.innerHTML = characters
    .filter((c) => isAliveStatus(c.status))
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    .map((c) => `<button type="button" class="pill" data-pill-value="${escapeHTML(c.name)}">${escapeHTML(c.name)}</button>`)
    .join('');
};

const openLogSessionModal = (record = null) => {
  logSessionForm?.reset();
  editingSessionId = record ? record.id : null;
  if (logSessionModalTitle) {
    logSessionModalTitle.textContent = record ? 'Edit Session' : 'Log a Session';
  }
  if (logSessionSubmitBtn) {
    logSessionSubmitBtn.textContent = record ? 'Save Changes' : 'Save Session';
  }
  renderCharactersPresentPills();
  if (sessionLocationGroup) {
    renderSingleSelectPillGroup(sessionLocationGroup, getLocationOptions(getState(), ''));
  }
  initFormPillGroups(logSessionModal);

  const numberInput = logSessionForm?.querySelector('[name="session_number"]');
  const dateInput = logSessionForm?.querySelector('[name="session_date_played"]');
  const inGameInput = logSessionForm?.querySelector('[name="in_game_session_ended"]');
  const notesInput = logSessionForm?.querySelector('[name="notes"]');

  if (record) {
    if (numberInput) numberInput.value = record.session_number || '';
    if (dateInput) dateInput.value = formatDateForInputValue(record.session_date_played);
    if (inGameInput) inGameInput.value = formatDateForInputValue(record.in_game_session_ended);
    if (notesInput) notesInput.value = record.notes || '';
    if (charactersPresentGroup) {
      const hidden = charactersPresentGroup.querySelector('input[type="hidden"]');
      const presentSet = new Set(
        String(record.characters_present || '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      );
      if (hidden) hidden.value = Array.from(presentSet).join(', ');
      updateFormPillSummary(charactersPresentGroup, presentSet);
    }
  } else if (dateInput) {
    dateInput.value = formatDateForInputValue(getToday());
  }

  logSessionModal?.showModal();
};

// Applies the session's Location and In-Game — Session Ended date to every
// character present: their Current Location is set to match the session's
// Location, and their Time Jail is set to the in-game end date (so they're
// locked until the shared campaign timeline catches up to that date).
const applySessionUpdatesToCharacters = async (formData) => {
  const presentNames = String(formData.characters_present || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  const location = String(formData.location || '').trim();
  const inGameDate = String(formData.in_game_session_ended || '').trim();
  if (!presentNames.length || (!location && !inGameDate)) return 0;

  const updates = {};
  if (location) updates.current_location = location;
  if (inGameDate) updates.time_jail = inGameDate;

  const { characters } = getState();
  let updatedCount = 0;
  await Promise.all(
    presentNames.map(async (name) => {
      const character = characters.find((c) => c.name === name);
      if (!character) return;
      try {
        await updateCharacter(character.id, updates);
        updateCharacterLocal(character.id, updates);
        updatedCount += 1;
      } catch (error) {
        console.error(`Failed to update ${name} from session log:`, error);
      }
    })
  );
  return updatedCount;
};

const handleLogSessionSubmit = async (event) => {
  event.preventDefault();
  const formData = Object.fromEntries(new FormData(logSessionForm).entries());
  if (!formData.session_date_played) {
    alert('Session Date Played is required.');
    return;
  }
  const payload = {
    session_number: formData.session_number || '',
    session_date_played: formData.session_date_played,
    in_game_session_ended: formData.in_game_session_ended || '',
    characters_present: formData.characters_present || '',
    notes: formData.notes || ''
  };
  try {
    if (editingSessionId) {
      const updated = await updateSession(editingSessionId, payload);
      updateSessionLocal(editingSessionId, updated);
      editingSessionId = null;
      logSessionModal?.close();
      alert('Session updated.');
      return;
    }
    const created = await createSession(payload);
    addSessionLocal(created);
    const updatedCount = await applySessionUpdatesToCharacters(formData);
    logSessionModal?.close();
    alert(
      updatedCount
        ? `Session logged. Updated ${updatedCount} character${updatedCount === 1 ? '' : 's'}.`
        : 'Session logged.'
    );
  } catch (error) {
    alert(`Failed to save session: ${error.message}`);
  }
};

const handleDeleteSession = async (id) => {
  const { sessions } = getState();
  const session = (sessions || []).find((s) => String(s.id) === String(id));
  const confirmed = window.confirm(`Delete ${session ? formatSessionLabel(session) : 'this session'}?`);
  if (!confirmed) return;
  try {
    await deleteSession(id);
    removeSessionLocal(id);
  } catch (error) {
    alert(`Failed to delete session: ${error.message}`);
  }
};

const openNeverForgetModal = (record = null) => {
  neverForgetForm?.reset();
  editingNeverForgetId = record ? record.id : null;
  if (neverForgetModalTitle) {
    neverForgetModalTitle.textContent = record ? 'Edit Never Forget' : 'Add a Never Forget';
  }
  if (neverForgetSubmitBtn) {
    neverForgetSubmitBtn.textContent = record ? 'Save Changes' : 'Save';
  }
  const nameInput = neverForgetForm?.querySelector('[name="event_name"]');
  const dateInput = neverForgetForm?.querySelector('[name="event_date"]');
  const descInput = neverForgetForm?.querySelector('[name="description"]');
  if (record) {
    if (nameInput) nameInput.value = record.event_name || '';
    if (dateInput) dateInput.value = record.event_date || '';
    if (descInput) descInput.value = record.description || '';
  } else if (dateInput) {
    dateInput.value = formatDateForInputValue(getToday());
  }
  neverForgetModal?.showModal();
};

const handleNeverForgetSubmit = async (event) => {
  event.preventDefault();
  const formData = Object.fromEntries(new FormData(neverForgetForm).entries());
  if (!formData.event_name || !formData.event_date) {
    alert('Event Name and Date are required.');
    return;
  }
  try {
    const payload = {
      event_name: formData.event_name,
      event_date: formData.event_date,
      description: formData.description || ''
    };
    if (editingNeverForgetId) {
      const updated = await updateNeverForgetEvent(editingNeverForgetId, payload);
      updateNeverForgetEventLocal(editingNeverForgetId, updated);
    } else {
      const created = await createNeverForgetEvent(payload);
      addNeverForgetEventLocal(created);
    }
    editingNeverForgetId = null;
    neverForgetModal?.close();
  } catch (error) {
    alert(`Failed to save Never Forget: ${error.message}`);
  }
};

export const initCalendar = () => {
  calendarTodayBtn?.addEventListener('click', goToToday);

  calendarPrevBtn?.addEventListener('click', () => {
    viewMonth -= 1;
    if (viewMonth < 0) {
      viewMonth = 11;
      viewYear -= 1;
    }
    selectedDate = null;
    renderCalendar();
  });

  calendarNextBtn?.addEventListener('click', () => {
    viewMonth += 1;
    if (viewMonth > 11) {
      viewMonth = 0;
      viewYear += 1;
    }
    selectedDate = null;
    renderCalendar();
  });

  calendarGrid?.addEventListener('click', (event) => {
    const cell = event.target.closest('.cal-day[data-date]');
    if (!cell) return;
    selectedDate = cell.dataset.date;
    renderCalendar();
  });

  logSessionBtn?.addEventListener('click', () => openLogSessionModal());
  logSessionCancel?.addEventListener('click', () => logSessionModal?.close());
  logSessionForm?.addEventListener('submit', handleLogSessionSubmit);
  logSessionModal?.addEventListener('close', () => {
    editingSessionId = null;
  });

  neverForgetBtn?.addEventListener('click', () => openNeverForgetModal());
  neverForgetCancel?.addEventListener('click', () => neverForgetModal?.close());
  neverForgetForm?.addEventListener('submit', handleNeverForgetSubmit);
  neverForgetModal?.addEventListener('close', () => {
    editingNeverForgetId = null;
  });

  calendarDetailRail?.addEventListener('click', (event) => {
    const editNeverForgetBtn = event.target.closest('[data-edit-neverforget-id]');
    if (editNeverForgetBtn) {
      const id = editNeverForgetBtn.dataset.editNeverforgetId;
      const { neverForgetEvents } = getState();
      const record = (neverForgetEvents || []).find((evt) => String(evt.id) === String(id));
      if (record) openNeverForgetModal(record);
      return;
    }
    const editSessionBtn = event.target.closest('[data-edit-session-id]');
    if (editSessionBtn) {
      const id = editSessionBtn.dataset.editSessionId;
      const { sessions } = getState();
      const record = (sessions || []).find((s) => String(s.id) === String(id));
      if (record) openLogSessionModal(record);
      return;
    }
    const deleteSessionBtn = event.target.closest('[data-delete-session-id]');
    if (deleteSessionBtn) {
      handleDeleteSession(Number(deleteSessionBtn.dataset.deleteSessionId));
    }
  });

  subscribe((state) => {
    if (state.activeTab === 'calendar' && calendarInitialized) renderCalendar();
  });
};
